import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';

// Use the logo from the public folder with absolute path
const LOGO_URL = process.env.PUBLIC_URL + '/logo.png';
const CURRENT_DATE = new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});

// Define styles for the PDF document
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 10,
    marginTop: 15, // Added margin top to push header down
    borderBottom: '1px solid #228B22',
  },
  logo: {
    width: 80,
    height: 30,
    marginBottom: 5,
    objectFit: 'contain',
  },
  headerRight: {
    textAlign: 'right',
  },
  title: {
    fontSize: 24,
    color: '#228B22',
    marginBottom: 10,
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
  },
  docTitle: {
    fontSize: 18,
    color: '#333',
    fontWeight: 'bold',
    paddingBottom: 5,
  },
  docSubtitle: {
    fontSize: 11,
    color: '#555',
    marginBottom: 15,
  },
  section: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#f8f8f8',
    borderRadius: 5,
    borderColor: '#e0e0e0',
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#228B22',
    marginBottom: 10,
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: 3,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: '25%',
    fontSize: 10,
    fontWeight: 'bold',
    color: '#555',
  },
  value: {
    width: '75%',
    fontSize: 10,
    color: '#333',
  },
  textBox: {
    marginVertical: 10,
    padding: 8,
    backgroundColor: 'white',
    borderRadius: 3,
    borderColor: '#e0e0e0',
    borderWidth: 1,
  },
  textContent: {
    fontSize: 10,
    color: '#333',
    lineHeight: 1.5,
  },
  priority: {
    padding: 4,
    borderRadius: 3,
    fontSize: 10,
    color: 'white',
    display: 'inline',
    textAlign: 'center',
    width: '50%',
  },
  highPriority: {
    backgroundColor: '#d32f2f',
  },
  mediumPriority: {
    backgroundColor: '#228B22',
  },
  lowPriority: {
    backgroundColor: '#1976d2',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    borderTop: '1px solid #e0e0e0',
    paddingTop: 10,
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  taskId: {
    position: 'absolute',
    top: 10,
    right: 150, 
    padding: 6,
    backgroundColor: '#eaf5ea',
    borderRadius: 4,
    color: '#228B22',
    fontSize: 10,
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #c8e6c9',
  },
  separator: {
    marginVertical: 10,
    borderBottom: '1px dashed #e0e0e0',
  },
  signature: {
    marginTop: 40,
    marginBottom: 10,
    paddingTop: 20,
    borderTop: '1px dotted #aaa',
    width: '60%',
  },
  signatureLabel: {
    fontSize: 8,
    color: '#777',
  },
  boxesContainer: {
    flexDirection: 'row',
    marginTop: 20,
    justifyContent: 'space-between',
  },
  signatureBox: {
    width: '45%',
    padding: 10,
    borderTop: '1px solid #333',
  },
  signatureText: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
    marginTop: 5,
  },
  watermark: {
    position: 'absolute',
    bottom: 120,
    right: 50,
    fontSize: 50,
    color: 'rgba(34, 139, 34, 0.05)',
    transform: 'rotate(-45deg)',
  },
  disclaimer: {
    fontSize: 8,
    color: '#666',
    marginTop: 20,
    marginBottom: 10,
    padding: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 3,
    borderLeft: '3px solid #228B22',
  },
  confidential: {
    position: 'absolute',
    top: 10,
    left: 30, // Changed from 'right' to 'left' to avoid overlap with task ID
    fontSize: 8,
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  timeStamp: {
    fontSize: 8,
    color: '#666',
    textAlign: 'right',
    marginTop: 5,
    fontStyle: 'italic',
  },
});

// Create the TaskPDF component
const TaskPDF = ({ taskData }) => {
  // Generate a unique task ID if one doesn't exist
  const taskId = taskData.TaskID ;
  
  // Format the current time for the timestamp
  const now = new Date();
  const timestamp = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;

  // Determine priority class
  const getPriorityClass = (priority) => {
    if (priority?.toLowerCase().startsWith('high')) return styles.highPriority;
    if (priority?.toLowerCase().startsWith('medium')) return styles.mediumPriority;
    return styles.lowPriority;
  };

  // Format date strings
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Extract complexity and days from priority string
  const getPriorityDetails = (priority) => {
    if (!priority) return { level: 'Medium', complexity: 'Medium', days: '3d' };
    
    const match = priority.match(/([^,]+),\s*([^()]+)\s*\((\w+)\)/);
    if (match) {
      return {
        level: match[1].trim(),
        complexity: match[2].trim(),
        days: match[3]
      };
    }
    
    return { level: priority, complexity: 'Medium', days: '3d' };
  };
  
  const priorityDetails = getPriorityDetails(taskData.priority);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Confidentiality Notice */}
        <Text style={styles.confidential}>CONFIDENTIAL - INTERNAL USE ONLY</Text>
        
        {/* Header with logo and document info */}
        <View style={styles.header}>
          <Image 
            src={LOGO_URL}
            style={styles.logo}
          />
          <View style={styles.headerRight}>
            <Text style={styles.docTitle}>Official Task Assignment</Text>
            <Text style={styles.docSubtitle}>Generated on {CURRENT_DATE}</Text>
          </View>
        </View>

        {/* Task ID Display (like a stamp) */}
        <View style={styles.taskId}>
          <Text>Task Number: {taskId}</Text>
        </View>

        {/* Title Section */}
        <View>
          <Text style={styles.title}>{taskData.title || 'New Task Assignment'}</Text>
          <Text style={styles.subtitle}>{taskData.department || 'Department'}</Text>
        </View>

        {/* Task Info Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Task Details</Text>
          
          <View style={styles.row}>
            <Text style={styles.label}>Priority Level:</Text>
            <View style={[styles.priority, getPriorityClass(taskData.priority)]}>
              <Text>{priorityDetails.level} - {priorityDetails.complexity} ({priorityDetails.days})</Text>
            </View>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Task Type:</Text>
            <Text style={styles.value}>{taskData.taskType || 'Not specified'}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Start Date:</Text>
            <Text style={styles.value}>{formatDate(taskData.startDate)}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Due Date:</Text>
            <Text style={styles.value}>{formatDate(taskData.dueDate)}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Man Days:</Text>
            <Text style={styles.value}>{taskData.TotalManDays || priorityDetails.days.replace('d', '')}</Text>
          </View>
        </View>

        {/* Description Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Description</Text>
          <View style={styles.textBox}>
            <Text style={styles.textContent}>{taskData.description || 'No description provided.'}</Text>
          </View>
        </View>

        {/* Expected Output Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Expected Output</Text>
          <View style={styles.textBox}>
            <Text style={styles.textContent}>{taskData.expectedOutput || 'No expected output defined.'}</Text>
          </View>
        </View>

        {/* Assignment Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Assignment Information</Text>
          
          <View style={styles.row}>
            <Text style={styles.label}>Created By:</Text>
            <Text style={styles.value}>{taskData.createdBy || 'Not specified'}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Assigned To:</Text>
            <Text style={styles.value}>{taskData.assignedTo || 'Not yet assigned'}</Text>
          </View>
          
          {taskData.assignedToStaffId && (
            <View style={styles.row}>
              <Text style={styles.label}>Staff ID:</Text>
              <Text style={styles.value}>{taskData.assignedToStaffId}</Text>
            </View>
          )}
          
          <View style={styles.row}>
            <Text style={styles.label}>Number of People:</Text>
            <Text style={styles.value}>{taskData.numberOfPeople || '1'}</Text>
          </View>
        </View>

        {/* Executive Disclaimer */}
        <View style={styles.disclaimer}>
          <Text>DISCLAIMER: This task assignment document is an official company record. All information contained herein is confidential and proprietary. 
          Tasks are assigned based on departmental priorities and company objectives. The estimated timelines and resource allocations are subject to change 
          based on evolving business needs. Any redistribution or reassignment of tasks must be approved by management. Resources assigned to this task are
          expected to adhere to all company policies and procedures while executing it.</Text>
        </View>

        {/* Signature Section */}
        <View style={styles.boxesContainer}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureText}>Authorized By: {taskData.createdBy}</Text>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.signatureText}>Accepted By: {taskData.assignedTo || '___________________'}</Text>
          </View>
        </View>

        <Text style={styles.timeStamp}>Document generated: {timestamp}</Text>

        {/* Watermark */}
        <Text style={styles.watermark}>AUJ Tasks</Text>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>AUJ Task Management System</Text>
          <Text>Page 1 of 1</Text>
          <Text>© {new Date().getFullYear()} AUJ Corporation. All rights reserved.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default TaskPDF;

// Export a function to generate and download PDF
export const generatePDF = async (taskData, options = {}) => {
  const blob = await pdf(<TaskPDF taskData={taskData} />).toBlob();
  const url = URL.createObjectURL(blob);
  
  // If download option is enabled, create a download link
  if (options.download) {
    // Create an anchor element and set properties for download
    const link = document.createElement('a');
    link.href = url;
    link.download = options.filename || `Task_${taskData.TaskID || 'New'}.pdf`;
    
    // Append to the body, click programmatically and then remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    
    return url;
  } else {
    // Open PDF in new tab with explicit options to ensure it opens in a new tab
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    // If browser blocked the new window, try to focus it
    if (newWindow) newWindow.focus();
    
    return url;
  }
};